import { useCallback, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import { logsApi } from '@carrent/api'
import { useAuthStore } from '@carrent/core'
import { formatDateTime } from '@carrent/domain'
import { EventTime, Icon, PageHeader } from '@carrent/ui'

/**
 * Просмотр журналов.
 *
 * Один экран на три потока. Фильтры живут в URL: ссылку на конкретную
 * выборку нужно уметь отправить коллеге, а «история» из карточки
 * машины — это тот же экран с преднастроенным фильтром.
 */

const STREAM_LABELS = {
  audit: 'Действия',
  access: 'Доступ',
  telemetry: 'Телеметрия',
}

const PERIODS = [
  { key: '1', label: 'Сутки' },
  { key: '7', label: 'Неделя' },
  { key: '30', label: 'Месяц' },
  { key: '', label: 'Всё время' },
]

function periodToSince(days) {
  if (!days) return undefined
  const d = new Date()
  d.setDate(d.getDate() - Number(days))
  return d.toISOString()
}

/** Колонки различаются по потокам: у них разные поля и разный смысл. */
const COLUMNS = {
  audit: [
    { key: 'created_at', label: 'Время' },
    { key: 'action', label: 'Действие' },
    { key: 'entity', label: 'Объект' },
    { key: 'actor_id', label: 'Кто' },
    { key: 'app', label: 'Откуда' },
    { key: 'changes', label: 'Изменения' },
  ],
  access: [
    { key: 'occurred_at', label: 'Время' },
    { key: 'event', label: 'Событие' },
    { key: 'result', label: 'Итог' },
    { key: 'user_id', label: 'Кто' },
    { key: 'ip_address', label: 'Адрес' },
    { key: 'reason', label: 'Причина' },
  ],
  telemetry: [
    { key: 'occurred_at', label: 'Время' },
    { key: 'event', label: 'Событие' },
    { key: 'app', label: 'Откуда' },
    { key: 'props', label: 'Детали' },
  ],
}

function ChangesCell({ entry }) {
  const before = entry.before || {}
  const after = entry.after || {}
  const keys = [...new Set([...Object.keys(before), ...Object.keys(after)])]
  if (keys.length === 0) return <span className="logs__muted">—</span>

  return (
    <span className="logs__changes">
      {keys.slice(0, 3).map((k) => (
        <span key={k} className="logs__change">
          <span className="logs__change-key">{k}</span>
          {k in before && <s className="logs__change-old">{String(before[k])}</s>}
          <span className="logs__change-new">{String(after[k] ?? '—')}</span>
        </span>
      ))}
      {keys.length > 3 && <span className="logs__muted">+{keys.length - 3}</span>}
    </span>
  )
}

export default function AdminLogsPage() {
  const { isAdmin } = useAuthStore()
  const [params, setParams] = useSearchParams()

  const stream = params.get('stream') || 'audit'
  const period = params.get('period') ?? '7'

  const [meta, setMeta] = useState(null)
  const [items, setItems] = useState([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [timeline, setTimeline] = useState(null)

  const query = {
    since: periodToSince(period),
    user_id: params.get('user_id') || undefined,
    app: params.get('app') || undefined,
    event: params.get('event') || undefined,
    entity_type: params.get('entity_type') || undefined,
    entity_id: params.get('entity_id') || undefined,
    ip: params.get('ip') || undefined,
  }

  useEffect(() => {
    logsApi.filters().then(({ data }) => setMeta(data.data)).catch(() => setMeta(null))
  }, [])

  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const { data } = await logsApi.list(stream, { ...query, page, per_page: 50 })
      setItems(data.data.items)
      setTotal(data.data.meta.total)
    } catch (e) {
      // Ошибку показываем текстом сервера: 422 объясняет, какой
      // именно фильтр указан неверно.
      setError(e.response?.data?.message || 'Не удалось загрузить журнал')
      setItems([])
    } finally {
      setLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stream, page, params])

  useEffect(() => {
    load()
  }, [load])

  const setFilter = (key, value) => {
    const next = new URLSearchParams(params)
    if (value) next.set(key, value)
    else next.delete(key)
    setParams(next)
    setPage(1)
  }

  const openSession = async (sessionId) => {
    if (!sessionId) return
    try {
      const { data } = await logsApi.session(sessionId)
      setTimeline(data.data)
    } catch {
      setError('Не удалось загрузить сессию')
    }
  }

  const availableStreams = meta?.streams || ['audit']
  const columns = COLUMNS[stream] || COLUMNS.audit

  return (
    <div className="flex-col gap">
      <PageHeader
        title="Журналы"
        right={
          <a
            className="btn btn--secondary btn--sm"
            href={logsApi.exportUrl(stream, query)}
            download
          >
            <Icon name="file" size={14} />
            Выгрузить CSV
          </a>
        }
      />

      <div className="toolbar">
        <div className="chip-row" role="tablist" aria-label="Поток">
          {availableStreams.map((s) => (
            <button
              key={s}
              type="button"
              role="tab"
              aria-selected={stream === s}
              className={`chip ${stream === s ? 'chip--active' : ''}`}
              onClick={() => setFilter('stream', s)}
            >
              {STREAM_LABELS[s] || s}
            </button>
          ))}
        </div>

        <div className="chip-row toolbar__end" role="tablist" aria-label="Период">
          {PERIODS.map((p) => (
            <button
              key={p.key || 'all'}
              type="button"
              role="tab"
              aria-selected={period === p.key}
              className={`chip ${period === p.key ? 'chip--active' : ''}`}
              onClick={() => setFilter('period', p.key)}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      <div className="logs__filters">
        {stream === 'audit' && (
          <label className="field logs__filter">
            <span className="field__label">Тип объекта</span>
            <select
              className="input"
              value={params.get('entity_type') || ''}
              onChange={(e) => setFilter('entity_type', e.target.value)}
            >
              <option value="">Любой</option>
              {(meta?.entity_types || []).map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </label>
        )}

        <label className="field logs__filter">
          <span className="field__label">Приложение</span>
          <select
            className="input"
            value={params.get('app') || ''}
            onChange={(e) => setFilter('app', e.target.value)}
          >
            <option value="">Любое</option>
            <option value="driver">Водитель</option>
            <option value="console">Консоль</option>
          </select>
        </label>

        {stream === 'access' && isAdmin() && (
          <label className="field logs__filter">
            <span className="field__label">IP-адрес</span>
            <input
              className="input"
              value={params.get('ip') || ''}
              onChange={(e) => setFilter('ip', e.target.value)}
              placeholder="1.2.3.4"
            />
          </label>
        )}

        {(params.get('entity_id') || params.get('user_id')) && (
          <button
            type="button"
            className="btn btn--ghost btn--sm logs__reset"
            onClick={() => {
              const next = new URLSearchParams(params)
              next.delete('entity_id')
              next.delete('entity_type')
              next.delete('user_id')
              setParams(next)
            }}
          >
            <Icon name="close" size={14} />
            Сбросить привязку к объекту
          </button>
        )}
      </div>

      {loading && (
        <div className="table-wrap">
          <div className="p-page flex-col gap-sm">
            <span className="skeleton table__skeleton-row" />
            <span className="skeleton table__skeleton-row" />
            <span className="skeleton table__skeleton-row" />
          </div>
        </div>
      )}

      {!loading && error && (
        <div className="state">
          <p className="state__title">{error}</p>
          <button type="button" className="btn btn--secondary" onClick={load}>
            Повторить
          </button>
        </div>
      )}

      {!loading && !error && items.length === 0 && (
        <div className="state">
          <Icon name="file" size={32} />
          <p className="state__title">Записей нет</p>
          <p className="state__text">Измените период или фильтры.</p>
        </div>
      )}

      {!loading && !error && items.length > 0 && (
        <>
          <div className="table-wrap">
            <table className="table table--dense">
              <thead>
                <tr>
                  {columns.map((c) => (
                    <th key={c.key} scope="col">{c.label}</th>
                  ))}
                  <th scope="col" className="table__col--actions">Сессия</th>
                </tr>
              </thead>
              <tbody>
                {items.map((entry) => (
                  <tr key={entry.id}>
                    <td className="logs__time">
                      {stream === 'audit'
                        ? <EventTime entry={entry} />
                        : formatDateTime(entry.occurred_at)}
                    </td>
                    <td>
                      <code className="logs__event">{entry.action || entry.event}</code>
                    </td>

                    {stream === 'audit' && (
                      <>
                        <td className="logs__muted">
                          {entry.entity_type
                            ? `${entry.entity_type} #${entry.entity_id}`
                            : '—'}
                        </td>
                        <td className="logs__muted">{entry.actor_id || 'система'}</td>
                        <td className="logs__muted">{entry.app || '—'}</td>
                        <td><ChangesCell entry={entry} /></td>
                      </>
                    )}

                    {stream === 'access' && (
                      <>
                        <td>
                          <span className={`badge badge--${
                            entry.result === 'success' ? 'success' : 'danger'}`}>
                            {entry.result}
                          </span>
                        </td>
                        <td className="logs__muted">
                          {entry.user_id || entry.login_hint || '—'}
                        </td>
                        <td className="logs__muted">{entry.ip_address || '—'}</td>
                        <td className="logs__muted">{entry.reason || '—'}</td>
                      </>
                    )}

                    {stream === 'telemetry' && (
                      <>
                        <td className="logs__muted">{entry.app || '—'}</td>
                        <td className="logs__muted">
                          {entry.props ? JSON.stringify(entry.props) : '—'}
                        </td>
                      </>
                    )}

                    <td>
                      {entry.session_id ? (
                        <button
                          type="button"
                          className="btn btn--ghost btn--sm"
                          onClick={() => openSession(entry.session_id)}
                          title="Показать все записи этой сессии"
                        >
                          <Icon name="search" size={13} />
                        </button>
                      ) : (
                        <span className="logs__muted">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="logs__pager">
            <span className="text-muted">
              {items.length} из {total}
            </span>
            <button
              type="button"
              className="btn btn--secondary btn--sm"
              disabled={page === 1}
              onClick={() => setPage((p) => p - 1)}
            >
              Назад
            </button>
            <button
              type="button"
              className="btn btn--secondary btn--sm"
              disabled={page * 50 >= total}
              onClick={() => setPage((p) => p + 1)}
            >
              Вперёд
            </button>
          </div>
        </>
      )}

      {timeline && (
        <SessionTimeline data={timeline} onClose={() => setTimeline(null)} />
      )}
    </div>
  )
}

/**
 * Все записи одной сессии.
 *
 * Увидев подозрительное действие, важно сразу понять, кто и откуда
 * вошёл в эту сессию — не собирая запрос вручную.
 */
function SessionTimeline({ data, onClose }) {
  return (
    <div
      className="sheet-overlay"
      role="dialog"
      aria-modal="true"
      aria-label="Записи сессии"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="sheet sheet--wide">
        <div className="sheet__header">
          <h3 className="sheet__title">Сессия {data.session_id?.slice(0, 8)}…</h3>
          <button type="button" className="btn-icon" onClick={onClose} aria-label="Закрыть">
            <Icon name="close" size={18} />
          </button>
        </div>

        {data.access && (
          <>
            <p className="section-title">Вход и доступ</p>
            <div className="logs__timeline">
              {data.access.length === 0 && <p className="text-muted">Записей нет</p>}
              {data.access.map((e) => (
                <div key={`a${e.id}`} className="logs__timeline-row">
                  <span className="logs__time">{formatDateTime(e.occurred_at)}</span>
                  <code className="logs__event">{e.event}</code>
                  <span className="logs__muted">{e.ip_address || '—'}</span>
                </div>
              ))}
            </div>
          </>
        )}

        <p className="section-title">Действия</p>
        <div className="logs__timeline">
          {data.audit.length === 0 && <p className="text-muted">Записей нет</p>}
          {data.audit.map((e) => (
            <div key={`d${e.id}`} className="logs__timeline-row">
              <EventTime entry={e} />
              <code className="logs__event">{e.action}</code>
              <span className="logs__muted">
                {e.entity_type ? `${e.entity_type} #${e.entity_id}` : ''}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
